import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { 
  MessageSquare, Check, X, Clock, Globe, Tag, ArrowLeftRight, 
  Loader2, AlertCircle, CheckCircle2, XCircle 
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { 
    badge: "bg-amber-100 text-amber-700", 
    label: "Oczekuje na odpowiedz",
    icon: Clock
  },
  counter_offer: { 
    badge: "bg-blue-100 text-blue-700", 
    label: "Kontroferta do rozpatrzenia",
    icon: ArrowLeftRight
  },
  accepted: { 
    badge: "bg-emerald-100 text-emerald-700", 
    label: "Zaakceptowana",
    icon: CheckCircle2
  },
  rejected: { 
    badge: "bg-red-100 text-red-600", 
    label: "Odrzucona",
    icon: XCircle
  },
};

export default function MyNegotiations() {
  const [negotiations, setNegotiations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/negotiations`);
      setNegotiations(res.data);
    } catch (err) {
      toast.error("Blad ladowania negocjacji");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAcceptCounter = async (neg) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/negotiations/${neg.id}/accept-counter`);
      toast.success("Kontroferta zaakceptowana! Mozesz teraz zamowic po nowej cenie.");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Blad akceptacji");
    } finally {
      setSubmitting(false);
      setConfirmDialog(null);
    }
  };

  const handleRejectCounter = async (negId) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/negotiations/${negId}/reject-counter`);
      toast.success("Kontroferta odrzucona - negocjacja wraca do admina");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Blad odrzucenia");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const pendingCount = negotiations.filter(n => n.status === "counter_offer").length;

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MessageSquare className="w-6 h-6" />
            Moje negocjacje
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Tutaj znajdziesz wszystkie swoje negocjacje cenowe
          </p>
        </div>
        {pendingCount > 0 && (
          <Badge variant="destructive" className="text-sm px-3 py-1">
            {pendingCount} kontrofert do rozpatrzenia
          </Badge>
        )}
      </div>

      {negotiations.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-medium text-muted-foreground">Brak negocjacji</h3>
            <p className="text-sm text-muted-foreground/70 mt-1">
              Przejdz do sklepu i kliknij "Negocjuj" przy wybranej ofercie
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {negotiations.map(neg => {
            const config = statusConfig[neg.status] || statusConfig.pending;
            const StatusIcon = config.icon;
            
            return (
              <Card 
                key={neg.id} 
                className={`transition-all ${
                  neg.status === "counter_offer" 
                    ? "border-blue-300 shadow-md ring-1 ring-blue-100" 
                    : neg.status === "accepted" || neg.status === "rejected"
                    ? "opacity-70"
                    : ""
                }`}
              >
                <CardContent className="py-5">
                  <div className="flex flex-col lg:flex-row lg:justify-between gap-4">
                    <div className="flex-1 space-y-3">
                      {/* Status */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={config.badge}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {config.label}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {new Date(neg.created_at).toLocaleDateString("pl-PL", {
                            day: "numeric",
                            month: "long",
                            year: "numeric"
                          })}
                        </span>
                      </div>
                      
                      {/* Info o ofercie */}
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-muted-foreground" />
                          <span className="font-semibold">{neg.site_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Tag className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{neg.offer_name}</span>
                        </div>
                      </div>
                      
                      {/* Ceny */}
                      <div className="flex items-center gap-6 text-sm">
                        <div>
                          <span className="text-muted-foreground">Cena katalogowa:</span>
                          <span className="font-medium ml-2">{neg.current_price} PLN</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Twoja propozycja:</span>
                          <span className="font-bold text-primary ml-2">{neg.proposed_price} PLN</span>
                        </div>
                      </div>
                      
                      {/* Twoja wiadomosc */}
                      {neg.message && (
                        <div className="bg-muted/30 rounded-lg p-3 text-sm">
                          <span className="text-muted-foreground">Twoja wiadomosc: </span>
                          {neg.message}
                        </div>
                      )}
                      
                      {/* KONTROFERTA */}
                      {neg.status === "counter_offer" && neg.counter_price && (
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                          <div className="flex items-center gap-2">
                            <ArrowLeftRight className="w-5 h-5 text-blue-600" />
                            <span className="font-semibold text-blue-800">
                              Otrzymales kontroferte!
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            <span className="text-sm text-blue-700">Proponowana cena:</span>
                            <span className="text-2xl font-bold text-blue-600">
                              {neg.counter_price} PLN
                            </span>
                          </div>
                          
                          {neg.counter_by_name && (
                            <p className="text-xs text-blue-600">
                              Od: {neg.counter_by_name} ({neg.counter_by_role === "admin" ? "Administrator" : "Wydawca"})
                            </p>
                          )}
                          
                          {neg.counter_message && (
                            <p className="text-sm text-blue-700 bg-blue-100 rounded-lg p-2">
                              "{neg.counter_message}"
                            </p>
                          )}
                          
                          <div className="flex gap-3 pt-2">
                            <Button
                              onClick={() => setConfirmDialog(neg)}
                              className="flex-1 bg-emerald-600 hover:bg-emerald-700 gap-2"
                              disabled={submitting}
                            >
                              <Check className="w-4 h-4" />
                              Akceptuje {neg.counter_price} PLN
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => handleRejectCounter(neg.id)}
                              className="flex-1 text-red-600 hover:text-red-700 gap-2"
                              disabled={submitting}
                            >
                              <X className="w-4 h-4" />
                              Odrzuc kontroferte
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      {/* ZAAKCEPTOWANA */}
                      {neg.status === "accepted" && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                            <span className="font-semibold text-emerald-800">
                              Negocjacja zaakceptowana!
                            </span>
                          </div>
                          <p className="text-sm text-emerald-700 mt-2">
                            Twoja nowa cena: <strong>{neg.final_price || neg.counter_price || neg.proposed_price} PLN</strong>
                          </p>
                          <p className="text-xs text-emerald-600 mt-1">
                            Mozesz teraz zamowic po tej cenie w sklepie.
                          </p>
                        </div>
                      )}
                      
                      {/* ODRZUCONA */}
                      {neg.status === "rejected" && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                          <div className="flex items-center gap-2">
                            <XCircle className="w-5 h-5 text-red-600" />
                            <span className="font-semibold text-red-800">
                              Negocjacja odrzucona
                            </span>
                          </div>
                          {neg.reject_reason && (
                            <p className="text-sm text-red-700 mt-2">
                              Powod: {neg.reject_reason}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog potwierdzenia akceptacji */}
      <Dialog open={!!confirmDialog} onOpenChange={() => setConfirmDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="w-5 h-5 text-emerald-600" />
              Potwierdz akceptacje kontroferty
            </DialogTitle>
          </DialogHeader>
          {confirmDialog && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                <p><span className="text-muted-foreground">Strona:</span> <strong>{confirmDialog.site_name}</strong></p>
                <p><span className="text-muted-foreground">Oferta:</span> {confirmDialog.offer_name}</p>
                <p><span className="text-muted-foreground">Cena katalogowa:</span> {confirmDialog.current_price} PLN</p>
                <p><span className="text-muted-foreground">Twoja propozycja:</span> {confirmDialog.proposed_price} PLN</p>
                <div className="pt-2 border-t mt-2">
                  <p className="text-lg">
                    <span className="text-muted-foreground">Twoja nowa cena:</span>{" "}
                    <strong className="text-emerald-600 text-xl">{confirmDialog.counter_price} PLN</strong>
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-2 text-sm text-muted-foreground">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <p>Po akceptacji ta cena bedzie dostepna dla Ciebie w sklepie przy tej ofercie.</p>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setConfirmDialog(null)} className="flex-1">
                  Anuluj
                </Button>
                <Button 
                  onClick={() => handleAcceptCounter(confirmDialog)} 
                  disabled={submitting}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Akceptuje
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
